<template>
  <div>
    <v-row class="ml-0">
      <v-col cols="2" />
      <v-col cols="1" class="d-flex justify-center align-center">Display</v-col>
      <v-col cols="3">
        <v-select v-model="xAxisField" :items="selectorValues"></v-select>
      </v-col>
      <v-col cols="1" class="d-flex justify-center align-center"> and </v-col>
      <v-col cols="3">
        <v-select v-model="yAxisField" :items="selectorValues"></v-select>
      </v-col>
      <v-col cols="2" />
    </v-row>
    <v-row
      v-if="isGraphValuesValid"
      class="ml-0"
      style="width: 100%; justify-content: center"
      ><FullscreenGraphComponent
        style="width: 100%; height: 300px"
        :traces="[
          ...traces,
          ...maneuverPoints,
          ...relativeDistance2DThresholdLegend
        ]"
        :layout="plotLayout"
        :maneuversDates="maneuverDatesWithAxis"
        v-on:legendClick="toggleRelativeDistance2DTreshold($event)"
      />
    </v-row>
  </div>
</template>

<script>
import dataTools from '../../tools/data.js'
import Plotly from 'plotly.js-dist'
import FullscreenGraphComponent from './FullscreenGraphComponent'
import { mapGetters } from 'vuex'

export default {
  name: 'EvasiveManeuverAxisComparator',
  props: {
    noManeuverRelativeDistanceResults: {
      type: Object,
      default: () => null
    },
    targetManeuverRelativeDistanceResults: {
      type: Object,
      default: () => null
    },
    targetAndChaserManeuverRelativeDistanceResults: {
      type: Object,
      default: () => null
    },
    targetRelativeDistanceAtBurns: {
      type: Object,
      default: () => null
    },
    targetImpulseManeuverReports: {
      type: Array,
      default: () => null
    },
    chaserRelativeDistanceAtBurns: {
      type: Object,
      default: () => null
    },
    chaserImpulseManeuverReports: {
      type: Array,
      default: () => null
    },
    fieldsToCompare: {
      type: Array,
      default: () => []
    },
    context: {
      type: String,
      default: 'ExtendedRelativeDistanceResults'
    },
    ranges: {
      type: Object,
      default: () => {}
    },
    maneuversDates: {
      type: Array,
      default: () => []
    }
  },
  components: { FullscreenGraphComponent },
  mixins: [],
  data() {
    return {
      expanded: 0,
      xAxisField: '',
      yAxisField: '',
      relativeDistanceThresholdColor: 'rgba(255,0,0,0.3)',
      relativeDistanceThresholdLegendText:
        'Minimum approach distance threshold',
      showRelativeDistance2DThreshold: true
    }
  },
  watch: {
    xValues: {
      handler() {
        this.drawGraph()
      },
      deep: true
    },
    yValues: {
      handler() {
        this.drawGraph()
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters({
      currentMission: 'currentMission'
    }),
    minimumApproachDistance() {
      return this.currentMission?.minimumApproachDistance ?? 0
    },
    selectorValues() {
      return this.fieldsToCompare.map((field) => {
        return {
          value: field,
          text: this.getLabelFromPath([field], this.context)
        }
      })
    },
    isGraphValuesValid() {
      return this.traces != null && this.traces.length > 0
    },
    noManeuverTrace() {
      return this.buildTraceFromResult(
        this.noManeuverRelativeDistanceResults,
        this.$vuetify.theme.dark ? 'white' : '#1E1E1E',
        'No maneuver',
        {
          dash: 'dot',
          width: 1
        }
      )
    },
    targetManeuverTrace() {
      return this.buildTraceFromResult(
        this.targetManeuverRelativeDistanceResults,
        '#5757ff',
        'Evasive'
      )
    },
    targetAndChaserManeuverTrace() {
      return this.buildTraceFromResult(
        this.targetAndChaserManeuverRelativeDistanceResults,
        '#8B0000',
        'Evasive + Chaser reaction'
      )
    },
    traces() {
      return [
        this.noManeuverTrace,
        this.targetManeuverTrace,
        this.targetAndChaserManeuverTrace
      ].filter((trace) => trace != null)
    },
    maneuverDatesWithAxis() {
      const xType = dataTools.getFieldTypeFromPath(
        [this.xAxisField],
        this.context
      )
      const yType = dataTools.getFieldTypeFromPath(
        [this.yAxisField],
        this.context
      )

      if (xType === 'datetime') {
        return this.maneuversDates.map((maneuverDates) => ({
          ...maneuverDates,
          axis: 'x'
        }))
      } else if (yType === 'datetime') {
        return this.maneuversDates.map((maneuverDates) => ({
          ...maneuverDates,
          axis: 'y'
        }))
      } else {
        return []
      }
    },
    plotLayout() {
      return {
        xaxis: {
          title: {
            text:
              this.getLabelFromPath([this.xAxisField], this.context) +
              this.getUnitLabelFromPath([this.xAxisField], this.context)
          },
          range: this.ranges[this.xAxisField]
        },
        yaxis: {
          title: {
            text:
              this.getLabelFromPath([this.yAxisField], this.context) +
              this.getUnitLabelFromPath([this.yAxisField], this.context)
          },
          range: this.ranges[this.yAxisField]
        },
        margin: { t: 20, r: 30 },
        shapes: this.showRelativeDistance2DThreshold
          ? this.minDistanceThresholdShape
          : [],
        hovermode: 'closest'
      }
    },
    targetManeuverPoints() {
      return [
        this.buildManeuver2Dpoints(
          this.targetRelativeDistanceAtBurns,
          this.targetImpulseManeuverReports,
          'Target Burns',
          'blue'
        )
      ]
    },
    minDistanceThresholdShape() {
      if (
        this.xAxisField === 'propagationDates' ||
        this.yAxisField === 'propagationDates' ||
        this.xAxisField === this.yAxisField
      ) {
        return []
      }
      return [
        {
          type: 'circle',
          xref: 'x',
          yref: 'y',
          fillcolor: this.relativeDistanceThresholdColor,
          line: {
            color: 'transparent'
          },
          x0: -this.minimumApproachDistance / 2000,
          y0: -this.minimumApproachDistance / 2000,
          x1: this.minimumApproachDistance / 2000,
          y1: this.minimumApproachDistance / 2000
        }
      ]
    },
    relativeDistance2DThresholdLegend() {
      if (this.minDistanceThresholdShape.length === 0) {
        return []
      } else {
        return [
          {
            x: [null],
            y: [null],
            type: 'bar',
            marker: {
              color: this.relativeDistanceThresholdColor,
              line: { color: 'transparent' }
            },
            opacity: this.relativeDistanceThresholdOpacity,
            name: this.relativeDistanceThresholdLegendText,
            showLegend: true
          }
        ]
      }
    },
    chaserManeuverPoints() {
      return [
        this.buildManeuver2Dpoints(
          this.chaserRelativeDistanceAtBurns,
          this.chaserImpulseManeuverReports,
          'Chaser Burns',
          'red'
        )
      ]
    },
    maneuverPoints() {
      return [...this.chaserManeuverPoints, ...this.targetManeuverPoints]
    }
  },
  methods: {
    buildTraceFromResult(
      relativeDistanceResult,
      color,
      name,
      line = undefined
    ) {
      if (
        relativeDistanceResult == null ||
        !this.xAxisField ||
        !this.yAxisField
      ) {
        return null
      }
      const xValuesNotConverted = relativeDistanceResult[this.xAxisField]
      const yValuesNotConverted = relativeDistanceResult[this.yAxisField]
      return {
        x: xValuesNotConverted.map((value) =>
          this.getDisplayValueFromPath(value, [this.xAxisField], this.context)
        ),
        y: yValuesNotConverted.map((value) =>
          this.getDisplayValueFromPath(value, [this.yAxisField], this.context)
        ),
        text: xValuesNotConverted.map(
          (_, index) =>
            '<b>Step: ' +
            (index + 1) +
            ' of ' +
            xValuesNotConverted.length +
            '</b><br><b>Date:</b> ' +
            relativeDistanceResult.propagationDates[index]
        ),
        hovertemplate:
          '%{text} ' +
          `<br><br><b>${this.getLabelFromPath(
            [this.xAxisField],
            this.context
          )}</b>: %{x}` +
          `<br><b>${this.getLabelFromPath(
            [this.yAxisField],
            this.context
          )}</b>: %{y}`,
        marker: {
          color: color
        },
        name: name,
        line: line
      }
    },

    buildManeuver2Dpoints(
      relativeDistanceAtBurns,
      impulseManeuverReports,
      name,
      color
    ) {
      if (
        relativeDistanceAtBurns == null ||
        !this.xAxisField ||
        !this.yAxisField ||
        !relativeDistanceAtBurns[this.xAxisField] ||
        !relativeDistanceAtBurns[this.yAxisField]
      ) {
        return {}
      } else {
        const xValuesNotConverted = relativeDistanceAtBurns[this.xAxisField]
        const yValuesNotConverted = relativeDistanceAtBurns[this.yAxisField]
        return {
          x: xValuesNotConverted.map((value) =>
            value == null
              ? null
              : this.getDisplayValueFromPath(
                  value,
                  [this.xAxisField],
                  this.context
                )
          ),
          y: yValuesNotConverted.map((value) =>
            value == null
              ? null
              : this.getDisplayValueFromPath(
                  value,
                  [this.yAxisField],
                  this.context
                )
          ),
          text: impulseManeuverReports.map((report, index) => {
            return (
              `Burn n°${index + 1}` +
              `<br><br><b>ΔV along T (m/s)</b>: ${dataTools.convertSi2Display(
                'velocity',
                report.deltaVAlongXAxis
              )}` +
              `<br><b>ΔV along N (m/s)</b>: ${dataTools.convertSi2Display(
                'velocity',
                report.deltaVAlongYAxis
              )}` +
              `<br><b>ΔV along W (m/s)</b>: ${dataTools.convertSi2Display(
                'velocity',
                report.deltaVAlongZAxis
              )}`
            )
          }),
          hovertemplate: '%{text} ',
          name: name,
          line: {
            color: 'transparent'
          },
          marker: {
            color: color
          }
        }
      }
    },
    toggleRelativeDistance2DTreshold(data) {
      if (
        data.data[data.curveNumber].name ===
        this.relativeDistanceThresholdLegendText
      ) {
        this.showRelativeDistance2DThreshold =
          !this.showRelativeDistance2DThreshold
      }
    },

    getUnitLabelFromPath(fieldPath, context) {
      const fieldType = dataTools.getFieldTypeFromPath(fieldPath, context, null)
      return dataTools.getUnitLabel(dataTools.getDefaultGuiUnit(fieldType))
    },
    getLabelFromPath(fieldPath, context) {
      return dataTools.getFieldLabelFromPath(fieldPath, context)
    },
    getDisplayValueFromPath(value, fieldPath, context) {
      const fieldType = dataTools.getFieldTypeFromPath(fieldPath, context, null)
      return dataTools.convertSi2gui(fieldType, value)
    }
  }
}
</script>
